import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Router,
  RouterOutlet,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from './core/auth/services/auth.service';
import { ThemeService } from './core/theme/theme.service';
import { UtmCaptureService } from './core/analytics/utm-capture.service';
import { AppLoadingScreen } from './shared/ui';
import { Toast } from './shared/ui/molecules/toast/toast';

/**
 * Maximum ms to wait for the first Angular router navigation to complete
 * before force-clearing the navigation loading gate. Guards hanging on
 * Firestore reads (onboardingGuard) with no cache hit could block this
 * indefinitely on cold offline start without this timeout.
 */
const FIRST_NAV_TIMEOUT_MS = 12_000;

@Component({
  imports: [RouterOutlet, Toast, AppLoadingScreen],
  selector: 'om-root',
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly firstNavigationResolved = signal(false);
  readonly showGlobalLoading = computed(
    () => !this.firstNavigationResolved() || this.authService.isLoading()
  );

  constructor() {
    // Initialize theme service eagerly so dark mode applies early
    inject(ThemeService);

    // Capture UTMs / referrer on first visit (first-touch attribution).
    inject(UtmCaptureService).capture();

    // Safety net: if the first navigation never completes (e.g. onboardingGuard
    // hangs on a Firestore read with no offline cache), force-clear the gate so
    // the app is at least partially usable instead of frozen on the loading screen.
    const navTimeoutId = setTimeout(() => {
      if (!this.firstNavigationResolved()) {
        console.warn(
          `[App] First navigation did not resolve within ${FIRST_NAV_TIMEOUT_MS}ms. ` +
          'Forcing navigation gate open. A guard may be hanging on a network request.'
        );
        this.firstNavigationResolved.set(true);
      }
    }, FIRST_NAV_TIMEOUT_MS);

    this.destroyRef.onDestroy(() => clearTimeout(navTimeoutId));

    this.router.events
      .pipe(takeUntilDestroyed())
      .subscribe((event) => {
        if (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
        ) {
          clearTimeout(navTimeoutId);
          this.firstNavigationResolved.set(true);
        }
      });
  }
}
